import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, DollarSign, Users, PieChart as PieChartIcon, Building2, Target } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import ROIChart from "@/components/analytics/ROIChart";
import CapitalOverTimeChart from "@/components/analytics/CapitalOverTimeChart";
import PredictiveAnalysis from "@/components/analytics/PredictiveAnalysis";
import InvestorPerformanceTable from "@/components/analytics/InvestorPerformanceTable";

export default function InvestmentAnalytics() {
  const { data: investments = [], isLoading: investmentsLoading } = useQuery({
    queryKey: ["investments"],
    queryFn: () => base44.entities.Investment.list("-investment_date"),
  });

  const { data: investors = [], isLoading: investorsLoading } = useQuery({
    queryKey: ["investors"],
    queryFn: () => base44.entities.Investor.list(),
  });

  const isLoading = investmentsLoading || investorsLoading;

  // Calculate key metrics
  const totalInvested = investments
    .filter(i => i.status === "active" || i.status === "exited")
    .reduce((sum, inv) => sum + (inv.investment_amount || 0), 0);

  // Realized gains from exits
  const exitedInvestments = investments.filter(i => i.status === "exited");
  const totalExitValue = exitedInvestments.reduce((sum, inv) => sum + (inv.exit_amount || 0), 0);
  const totalInvestedInExited = exitedInvestments.reduce((sum, inv) => sum + (inv.investment_amount || 0), 0);
  const realizedGain = totalExitValue - totalInvestedInExited;

  // Unrealized gains from active investments with valuations
  const activeInvestments = investments.filter(i => i.status === "active" && i.current_valuation);
  const totalActiveInvested = activeInvestments.reduce((sum, inv) => sum + (inv.investment_amount || 0), 0);
  const totalActiveValue = activeInvestments.reduce((sum, inv) => sum + (inv.current_valuation || 0), 0);
  const unrealizedGain = totalActiveValue - totalActiveInvested;

  // Total ROI (realized + unrealized)
  const combinedInvested = totalInvestedInExited + totalActiveInvested;
  const combinedValue = totalExitValue + totalActiveValue;
  const overallROI = combinedInvested > 0 
    ? (((combinedValue - combinedInvested) / combinedInvested) * 100) 
    : 0;

  const activeCount = investments.filter(i => i.status === "active").length;
  const exitedCount = exitedInvestments.length;
  const valuatedCount = activeInvestments.length;

  // Group by investor type
  const investorsByType = investors.reduce((acc, inv) => {
    acc[inv.type] = (acc[inv.type] || 0) + 1;
    return acc;
  }, {});

  const typeLabels = {
    holding_company: "Holdingselskap",
    angel_investor: "Angel Investor",
    private_equity: "Private Equity",
    family_office: "Family Office",
    personal_investor: "Personlig Investor",
  };

  const investorTypeData = Object.entries(investorsByType).map(([type, count]) => ({
    name: typeLabels[type] || type,
    value: count,
  }));

  // Group investments by company
  const companyCounts = investments.reduce((acc, inv) => {
    acc[inv.company_name] = (acc[inv.company_name] || 0) + 1;
    return acc;
  }, {});

  const topCompanies = Object.entries(companyCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const COLORS = ["#c9e25a", "#a8b545", "#cfd8a0", "#f59e0b", "#10b981", "#6b7280", "#ef4444", "#a8b545"];

  const stats = [
    {
      label: "Total investert kapital",
      value: `${(totalInvested / 1000000).toFixed(1)}M kr`,
      icon: DollarSign,
      chip: "bg-brand-limeSoft text-brand-limeDark",
      subtitle: `${investments.length} investeringer`,
    },
    {
      label: "Total ROI",
      value: `${overallROI.toFixed(1)}%`,
      icon: TrendingUp,
      chip: overallROI >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600",
      subtitle: `${(combinedValue / 1000000).toFixed(1)}M kr total verdi`,
    },
    {
      label: "Aktive investorer",
      value: investors.filter(i => i.status === "invested").length,
      icon: Users,
      chip: "bg-brand-ink text-brand-lime",
      subtitle: `av ${investors.length} totalt`,
    },
    {
      label: "Antall investeringer",
      value: investments.length,
      icon: Building2,
      chip: "bg-amber-50 text-amber-600",
      subtitle: `${activeCount} aktive, ${exitedCount} avsluttet`,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4 animate-fade-in">
          <div className="w-12 h-12 rounded-2xl bg-brand-ink text-brand-lime flex items-center justify-center shrink-0">
            <PieChartIcon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-3xl font-extrabold text-brand-ink tracking-tight">Investeringsanalyse</h1>
            <p className="text-slate-500 mt-0.5">Dyptgående innsikt i investeringsporteføljen</p>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-white rounded-xl animate-pulse" />
              ))}
            </div>
            <div className="h-96 bg-white rounded-xl animate-pulse" />
          </div>
        ) : (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {stats.map((stat, i) => (
                <Card
                  key={i}
                  className="group p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 animate-fade-in"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="flex items-start justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{stat.label}</p>
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${stat.chip}`}>
                      <stat.icon className="w-[18px] h-[18px]" />
                    </div>
                  </div>
                  <p className="mt-4 text-2xl font-extrabold text-brand-ink tracking-tight">{stat.value}</p>
                  {stat.subtitle && <p className="mt-1 text-xs text-slate-400">{stat.subtitle}</p>}
                </Card>
              ))}
            </div>

            {/* Top Companies & Investor Types */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card className="p-6 bg-white border-0 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Selskaper vi har hentet penger til</h3>
                {topCompanies.length > 0 ? (
                  <div className="space-y-3">
                    {topCompanies.map((company, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-lime-100 flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-zinc-900" />
                          </div>
                          <span className="font-medium text-slate-900">{company.name}</span>
                        </div>
                        <Badge variant="secondary">{company.count} investering{company.count > 1 ? 'er' : ''}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">Ingen investeringer registrert</p>
                )}
              </Card>

              <Card className="p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-brand-ink mb-4">Type investorer</h3>
                {investorTypeData.length > 0 ? (
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <div className="relative w-[200px] h-[200px] shrink-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={investorTypeData}
                            cx="50%"
                            cy="50%"
                            innerRadius={62}
                            outerRadius={92}
                            paddingAngle={3}
                            cornerRadius={6}
                            dataKey="value"
                            stroke="none"
                          >
                            {investorTypeData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-3xl font-extrabold text-brand-ink leading-none">{investors.length}</span>
                        <span className="text-[11px] text-slate-400 mt-1">investorer</span>
                      </div>
                    </div>
                    <div className="flex-1 w-full space-y-2">
                      {investorTypeData.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-slate-600">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[index % COLORS.length] }} />
                            {entry.name}
                          </span>
                          <span className="font-semibold text-brand-ink">{entry.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">Ingen investorer registrert</p>
                )}
              </Card>
            </div>

            {/* Investment Portfolio Overview */}
            <Card className="p-6 bg-white border-0 shadow-sm mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Porteføljesammendrag</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Realisert Gevinst</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {(realizedGain / 1000000).toFixed(1)}M kr
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Fra {exitedCount} avsluttede investeringer</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Urealisert Gevinst</p>
                  <p className="text-2xl font-bold text-zinc-900">
                    {(unrealizedGain / 1000000).toFixed(1)}M kr
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Fra {valuatedCount} verdivurderte investeringer</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Total Porteføljeverdi</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {(combinedValue / 1000000).toFixed(1)}M kr
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Investert: {(combinedInvested / 1000000).toFixed(1)}M kr</p>
                </div>
              </div>
            </Card>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <ROIChart investments={investments} investors={investors} />
              <CapitalOverTimeChart investments={investments} />
            </div>

            {/* Investor Performance Table */}
            <InvestorPerformanceTable investments={investments} investors={investors} />

            {/* Predictive Analysis */}
            <PredictiveAnalysis investments={investments} investors={investors} />
          </>
        )}
      </div>
    </div>
  );
}